import type { OptionProps } from '@rover/kibble/patterns/chips/Chip';
import { CoatTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/coatTypeEnum';
import { GroomingTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/groomingTypeEnum';
import { ProfessionalExperienceEnum } from '@rover/rsdk/src/apiClient/latest/model/professionalExperienceEnum';
import {
  type DayTimePreferenceKey,
  Days,
  DayTimePreferenceKeys,
} from '@rover/types/src/AvailabilityPreferences';

/**
 * Day labels mapping
 */
const DAY_LABELS: Record<Days, string> = {
  [Days.sunday]: 'Sun',
  [Days.monday]: 'Mon',
  [Days.tuesday]: 'Tue',
  [Days.wednesday]: 'Wed',
  [Days.thursday]: 'Thu',
  [Days.friday]: 'Fri',
  [Days.saturday]: 'Sat',
};

/**
 * For now, this maps the API enum values directly to display labels, until we
 * retrieve these descriptions from the API directly.
 *
 * @todo Add translations [DEV-137161](https://roverdotcom.atlassian.net/browse/DEV-137161)
 */
const TIME_SLOT_LABELS: Record<DayTimePreferenceKey, string> = {
  [DayTimePreferenceKeys.morningAvailability]: '9 am - 12 pm',
  [DayTimePreferenceKeys.middayAvailability]: '12 pm - 3 pm',
  [DayTimePreferenceKeys.eveningAvailability]: '3 pm - 7 pm',
};

/**
 * Generate days options from Days enum
 */
export const DAYS_OF_WEEK_OPTIONS: OptionProps[] = Object.values(Days).map((day) => ({
  id: day,
  value: day,
  label: DAY_LABELS[day],
}));

// Generate time slot options from schema enum
export const TIME_SLOT_OPTIONS: OptionProps[] = Object.values(DayTimePreferenceKeys).map(
  (timeSlot) => ({
    id: timeSlot,
    value: timeSlot,
    label: TIME_SLOT_LABELS[timeSlot],
  })
);

// Size constants
export const SIZE_XS = 'x-small';
export const SIZE_S = 'small';
export const SIZE_M = 'medium';
export const SIZE_L = 'large';
export const SIZE_XL = 'x-large';
export const SIZE_GIANT = 'giant';

// Map rate size to size label, full name, and weight range
export const RATE_SIZE_INFO: Record<
  string,
  { label: string; fullName: string; weightRange: { min: number; max: number | null } }
> = {
  [SIZE_XS]: { label: 'XS', fullName: 'Extra Small', weightRange: { min: 0, max: 15 } },
  [SIZE_S]: { label: 'S', fullName: 'Small', weightRange: { min: 16, max: 30 } },
  [SIZE_M]: { label: 'M', fullName: 'Medium', weightRange: { min: 31, max: 50 } },
  [SIZE_L]: { label: 'L', fullName: 'Large', weightRange: { min: 51, max: 75 } },
  [SIZE_XL]: { label: 'XL', fullName: 'Extra Large', weightRange: { min: 76, max: 100 } },
  [SIZE_GIANT]: { label: '2XL', fullName: 'Giant', weightRange: { min: 101, max: null } },
};

export const formatSizeWeight = (size: string): string => {
  const range = RATE_SIZE_INFO[size]?.weightRange;
  if (!range) return '';
  const { min, max } = range;
  return `${max === null ? `${min}+` : `${min}-${max}`} lbs`;
};

export const formatSizeLabelWithWeight = (size: string): string => {
  const info = RATE_SIZE_INFO[size];
  return info ? `${info.label} (${formatSizeWeight(size)})` : '';
};

// Order of rate sizes for sorting
export const RATE_SIZE_ORDER = [SIZE_XS, SIZE_S, SIZE_M, SIZE_L, SIZE_XL, SIZE_GIANT];

export const COAT_TYPE_ORDER: CoatTypeEnum[] = [
  CoatTypeEnum.short,
  CoatTypeEnum.long,
  CoatTypeEnum.complex,
];

export const COAT_TYPE_LABELS: Record<CoatTypeEnum, string> = {
  [CoatTypeEnum.short]: 'Short',
  [CoatTypeEnum.long]: 'Long',
  [CoatTypeEnum.complex]: 'Complex',
};

// Rates Service Types
export enum GroomingServiceType {
  BATH_AND_BRUSH = 'bath_and_brush',
  FULL_GROOM = 'full_groom',
}

// Map rate service type to prefix and display name.
export const SERVICE_TYPE_CONFIG: Record<
  GroomingServiceType,
  {
    prefix: string;
    displayName: string;
  }
> = {
  [GroomingServiceType.BATH_AND_BRUSH]: {
    prefix: 'bath',
    displayName: 'Bath and Brush',
  },
  [GroomingServiceType.FULL_GROOM]: {
    prefix: 'haircut',
    displayName: 'Full Groom',
  },
} as const;

export const getRateSlug = (size: string, serviceType: GroomingServiceType): string =>
  `${size}-${SERVICE_TYPE_CONFIG[serviceType].prefix}`;

export const SERVICE_TYPE_TO_GROOMING_TYPE: Record<GroomingServiceType, GroomingTypeEnum> = {
  [GroomingServiceType.BATH_AND_BRUSH]: GroomingTypeEnum.bath_and_brush,
  [GroomingServiceType.FULL_GROOM]: GroomingTypeEnum.full_groom,
};

// Bather experience values — mirrors backend BATHER_PROFESSIONAL_EXPERIENCES
export const BATHER_EXPERIENCES = new Set<string>([
  ProfessionalExperienceEnum['bather-more-than-one-year'],
  ProfessionalExperienceEnum['bather-6-months-to-1-year'],
  ProfessionalExperienceEnum['bather-3-months-to-6-months'],
  ProfessionalExperienceEnum['bather-less-than-3-months'],
]);
